<script setup lang="ts">
import { tracking } from '~/services/tracking.client'

import {computed, onBeforeUnmount, onMounted, ref} from 'vue'

interface BannerSlot {
    id: string
    label: string
    web: string
    mobile: string
    url: string
    fit: string
}

interface BannerInterface {
    bannerHeight: number
    bannerHeightMobile: number
    slots: BannerSlot[]
}

const props = defineProps<{
    config: BannerInterface,
    class?:string,
    id?:string|number
}>()

const main = computed(() =>
    props.config.slots?.filter((i) => i.id.includes('main-') && (i.web || i.mobile)) || []
)
const top = computed(() =>
    props.config.slots.find((i) => i.id === 'top-right' && (i.web || i.mobile))
)
const bottom = computed(() =>
    props.config.slots.find((i) => i.id === 'bottom-right' && (i.web || i.mobile))
)

const hasCarousel = computed(() => main.value.length > 0)

const currentIndex = ref(0)

const nextSlide = () => {
    if (!hasCarousel.value) return
    currentIndex.value = (currentIndex.value + 1) % main.value.length
}
const goToSlide = (index: number) => {
    if (!hasCarousel.value) return
    currentIndex.value = index

    if (main.value.length > 1) {
        stopAutoPlay()
        startAutoPlay()
    }
}


let intervalId: number | undefined

const startAutoPlay = () => {
    if (main.value.length <= 1) return
    stopAutoPlay()
    intervalId = window.setInterval(nextSlide, 5000)
}

const stopAutoPlay = () => {
    if (intervalId) {
        clearInterval(intervalId)
        intervalId = undefined
    }
}

onMounted(startAutoPlay)
onBeforeUnmount(stopAutoPlay)
const promotionRoot = ref<HTMLElement>()
let promotionObserver: IntersectionObserver | undefined
onMounted(() => {
    if (!promotionRoot.value || !('IntersectionObserver' in window)) return
    const seen = new Set<string>()
    promotionObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
            const target = entry.target as HTMLElement
            const id = target.dataset.promotionId
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5 && id && !seen.has(id)) {
                seen.add(id)
                tracking.promotion('view_promotion', id, target.dataset.promotionName || id)
            }
        }
    }, { threshold: 0.5 })
    promotionRoot.value.querySelectorAll('[data-promotion-id]').forEach(element => promotionObserver!.observe(element))
})
onBeforeUnmount(() => promotionObserver?.disconnect())
</script>

<template>
    <div
      role="banner" ref="promotionRoot"
            class="banner1"
            :class="props?.class || ''"
            :style="{
      '--banner-height': config.bannerHeight,
      '--banner-height-mobile': config.bannerHeightMobile
    }"
    >
        <!-- LEFT: big carousel -->
        <div v-if="hasCarousel" class="banner1__left" >
            <div class="banner1__slides">
                <div
                        class="banner1__track"
                        :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
                >
                    <NuxtLinkLocale
                            v-for="(item, index) in main"
                            :key="item.id"
                            :to="item.url || '/'" :data-promotion-id="`${props.id || 'banner'}:${item.id}`" :data-promotion-name="item.label" @click="tracking.promotion('select_promotion', `${props.id || 'banner'}:${item.id}`, item.label)"
                            class="banner1__slide"
                    >
                        <picture>
                            <source
                                    v-if="item.mobile"
                                    :srcset="item.mobile"
                                    media="(max-width: 992px)"
                            >
                            <source
                                    v-if="item.web"
                                    :srcset="item.web"
                                    media="(min-width: 993px)"
                            >
                          <img
                            class="banner1__img"
                            :src="item.web || item.mobile"
                            :alt="item.label"
                            :style="{ objectFit: item.fit || 'contain' }"
                            :loading="index === 0 ? 'eager' : 'lazy'"
                            :fetchpriority="index === 0 ? 'high' : undefined"
                            decoding="async"
                          >
                        </picture>
                    </NuxtLinkLocale>
                </div>
            </div>

            <!-- dots -->
            <div v-if="main.length > 1" class="banner1__dots">
                <button
                        v-for="(item, index) in main"
                        :key="item.id"
                        type="button"
                        class="banner1__dot"
                        :class="{ 'banner1__dot--active': index === currentIndex }"
                        @click="goToSlide(index)"
                />
            </div>
        </div>

        <!-- RIGHT: top & bottom images (hidden on mobile) -->
        <div class="banner1__right">
            <NuxtLinkLocale
                    v-if="top"
                    :to="top.url || '/'" :data-promotion-id="`${props.id || 'banner'}:${top.id}`" :data-promotion-name="top.label" @click="tracking.promotion('select_promotion', `${props.id || 'banner'}:${top.id}`, top.label)"
                    class="banner1__side banner1__side--top"
            >
                <img
                        class="banner1__img"
                        :src="top.web || top.mobile"
                        :alt="top.label"
                        :style="{ objectFit: top.fit || 'cover' }"
                        loading="lazy"
                >
            </NuxtLinkLocale>

            <NuxtLinkLocale
                    v-if="bottom"
                    :to="bottom.url || '/'" :data-promotion-id="`${props.id || 'banner'}:${bottom.id}`" :data-promotion-name="bottom.label" @click="tracking.promotion('select_promotion', `${props.id || 'banner'}:${bottom.id}`, bottom.label)"
                    class="banner1__side banner1__side--bottom"
            >

                <img
                        class="banner1__img"
                        :src="bottom.web || bottom.mobile"
                        :alt="bottom.label"
                        :style="{ objectFit: bottom.fit || 'cover' }"
                        loading="lazy"
                >
            </NuxtLinkLocale>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.banner1 {
  display: flex;
  gap: 16px;
  height: var(--banner-height);
  width: 100%;
  overflow: hidden;
  @media (max-width: 992px) {
    padding-left:0;
    padding-right:0;
  }
}

/* Ляво: голямо карусел изображение */
.banner1__left {
  position: relative;
  flex: 2 1 0;
  height: 100%;
  overflow: hidden;
}

.banner1__slides {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.banner1__track {
  display: flex;
  height: 100%;
  width: 100%;
  transition: transform 0.6s ease;
}

.banner1__slide {
  flex: 0 0 100%;
  height: 100%;
}

.banner1__img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  @media (max-width: 992px) {
    object-position: center;
    object-fit: contain !important;
  }
}

/* Dots */
.banner1__dots {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
}

.banner1__dot {
  all: unset;
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;

  &--active {
    background: rgba(255, 255, 255, 1);
  }
}

/* Дясно: две картинки една над друга */
.banner1__right {
  flex: 1 1 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.banner1__side {
  flex: 1;
  position: relative;
  overflow: hidden;
  display: block;

  .banner1__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

/* Mobile: само карусела, full-width */
@media (max-width: 992px) {
  .banner1 {
    height: var(--banner-height-mobile);
  }

  .banner1__right {
    display: none;
  }

  .banner1__left {
    flex: 1 1 100%;
    height: 100%;
  }
}
</style>




